import { useMemo, useState } from 'react';
import EmptyState from '../ui/EmptyState';

// Filter chips, in display order. An entry belongs to the first group whose test matches its
// `action` (e.g. "step.approval_approved"); the rest fall under "Other".
const GROUPS = [
  { value: 'completed', label: 'Step completed', test: (a) => /^step\.(completed|passed|moved_on|skipped)$/.test(a) },
  { value: 'approved', label: 'Approved', test: (a) => /approved$/.test(a) },
  { value: 'rejected', label: 'Rejected', test: (a) => /rejected$/.test(a) },
  { value: 'requests', label: 'Requests', test: (a) => /requested$/.test(a) || /_request\./.test(a) },
  { value: 'documents', label: 'Documents', test: (a) => a.startsWith('document.') },
  { value: 'assignments', label: 'Assignments', test: (a) => /^step\.(assigned|reassigned)$/.test(a) },
  { value: 'status', label: 'Status changes', test: (a) => /^candidate\.(withdrawn|suspended|suspended_reverted|reopened|rejected|hold|hold_reverted|withdrawn_reverted)$/.test(a) || a === 'step.reverted' },
  { value: 'profile', label: 'Profile', test: (a) => /^(candidate\.(created|updated|category_changed|vehicle_type_converted|deleted)|lead\.converted)$/.test(a) },
];
const OTHER = { value: 'other', label: 'Other', test: () => true };

const groupOf = (action = '') => GROUPS.find((g) => g.test(action)) || OTHER;

export default function ActivityTab({ candidate }) {
  const [filter, setFilter] = useState('all');
  const entries = candidate.recent_activity;

  const counts = useMemo(() => {
    const c = {};
    entries.forEach((e) => {
      const key = groupOf(e.action).value;
      c[key] = (c[key] || 0) + 1;
    });
    return c;
  }, [entries]);

  if (entries.length === 0) {
    return <EmptyState title="No activity recorded yet." />;
  }

  const chips = [{ value: 'all', label: 'All', count: entries.length }, ...[...GROUPS, OTHER].filter((g) => counts[g.value]).map((g) => ({ ...g, count: counts[g.value] }))];
  const shown = filter === 'all' ? entries : entries.filter((e) => groupOf(e.action).value === filter);

  return (
    <div>
      <div className="mb-3 flex flex-wrap gap-1.5">
        {chips.map((chip) => (
          <button
            key={chip.value}
            type="button"
            onClick={() => setFilter(chip.value)}
            className={`rounded-full border px-2.5 py-1 text-xs font-medium ${
              filter === chip.value
                ? 'border-primary bg-primary text-white'
                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            {chip.label} <span className={filter === chip.value ? 'text-white/80' : 'text-gray-400'}>{chip.count}</span>
          </button>
        ))}
      </div>

      <ul className="space-y-3">
        {shown.map((entry) => (
          <li key={entry.id} className="border-b border-gray-100 pb-3 last:border-0">
            <p className="text-sm text-gray-800">{entry.description}</p>
            <p className="text-xs text-gray-400">{new Date(entry.created_at).toLocaleString()}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}
